import { createElement, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

const drafts = new Set();
const hasDraft = () => [...drafts].some(draft => draft.dirty && draft.signature !== draft.savedSignature);
let permit = false;
let prompt = null;

function requestLeave(event, proceed) {
  if (event.reachLeaveAccepted) return false;
  if (permit) { permit = false; event.reachLeaveAccepted = true; return false; }
  if (!hasDraft()) return false;
  event.preventDefault(); event.stopImmediatePropagation();
  if (prompt) return true;
  const previous = document.activeElement;
  const overflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  const host = document.createElement('div');
  document.body.appendChild(host);
  const root = createRoot(host);
  const close = () => { root.unmount(); host.remove(); prompt = null; document.body.style.overflow = overflow; previous?.focus(); };
  const cancel = () => close();
  const leave = () => { close(); permit = true; proceed(); };
  prompt = { cancel };
  root.render(createElement('div', { className: 'o-scrim', role: 'presentation', onClick: cancel },
    createElement('div', { className: 'o-modal', role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'reach-unsaved-title',
      onClick: event => event.stopPropagation(), onKeyDown: event => {
        if (event.key === 'Escape') { event.preventDefault(); cancel(); }
        if (event.key === 'Tab') {
          const buttons = [...event.currentTarget.querySelectorAll('button')];
          if (event.shiftKey && document.activeElement === buttons[0]) { event.preventDefault(); buttons.at(-1)?.focus(); }
          else if (!event.shiftKey && document.activeElement === buttons.at(-1)) { event.preventDefault(); buttons[0]?.focus(); }
        }
      } },
      createElement('div', { className: 'o-modal-head' }, createElement('h2', { id: 'reach-unsaved-title', className: 'o-title' }, 'Leave without saving?')),
      createElement('div', { className: 'o-modal-body' }, createElement('p', { className: 'o-note' }, 'Your unsaved Reach changes will be lost. Keep editing to save them first.')),
      createElement('div', { className: 'o-modal-foot' },
        createElement('button', { type: 'button', className: 'o-btn', autoFocus: true, onClick: cancel }, 'Keep editing'),
        createElement('button', { type: 'button', className: 'o-btn o-btn-primary', onClick: leave }, 'Leave without saving')))));
  return true;
}

// Module-local protection: shared routing/workspace files stay untouched. No draft or
// credential is persisted. Successful saves disarm the warning before navigation.
export function useUnsavedChanges(dirty, signature) {
  const draft = useRef({ dirty, signature, savedSignature: Symbol('not saved') });
  draft.current.dirty = dirty;
  draft.current.signature = signature;
  useEffect(() => {
    drafts.add(draft.current);
    let previousUrl = window.location.href;
    let workspace = document.querySelector('select[aria-label="Active workspace"]')?.value;
    const leave = event => {
      if (!hasDraft()) return;
      event.preventDefault(); event.returnValue = '';
    };
    const click = event => {
      if (event.defaultPrevented || event.reachLeaveAccepted) return;
      const target = event.target.closest?.('[data-reach-leave], .o-topnav button, .o-topnav a, .o-subtab, a[href]');
      if (target && !target.disabled && !requestLeave(event, () => target.click())) {
        event.reachLeaveAccepted = true; window.setTimeout(() => { previousUrl = window.location.href; }, 0);
      }
    };
    const change = event => {
      if (event.reachLeaveAccepted) return;
      if (!event.target.matches?.('select[aria-label="Active workspace"]')) return;
      const select = event.target, next = select.value;
      if (requestLeave(event, () => { select.value = next; select.dispatchEvent(new Event('change', { bubbles: true })); })) select.value = workspace;
      else { workspace = next; event.reachLeaveAccepted = true; }
    };
    const pop = event => {
      if (event.reachLeaveAccepted) { previousUrl = window.location.href; return; }
      const nextUrl = window.location.href;
      if (nextUrl !== previousUrl && requestLeave(event, () => { history.pushState(null, '', nextUrl); window.dispatchEvent(new PopStateEvent('popstate')); })) history.pushState(null, '', previousUrl);
      else { previousUrl = nextUrl; event.reachLeaveAccepted = true; }
    };
    window.addEventListener('beforeunload', leave);
    document.addEventListener('click', click, true);
    document.addEventListener('change', change, true);
    window.addEventListener('popstate', pop, true);
    return () => {
      drafts.delete(draft.current);
      if (!hasDraft()) prompt?.cancel();
      window.removeEventListener('beforeunload', leave);
      document.removeEventListener('click', click, true);
      document.removeEventListener('change', change, true);
      window.removeEventListener('popstate', pop, true);
    };
  }, []);
  return (savedSignature = draft.current.signature) => { draft.current.savedSignature = savedSignature; };
}
